import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseOverviewStore, {
    type ApiResponse,
    type BaseStoreState,
} from '../../../shared/stores/baseOverviewStore';
import { formatName } from '../../../utils/helper';

interface EmployeeStoreState extends BaseStoreState {
    filterManager: any;
    filterTraining: any;
    filterTrainingStatus: number;
}

/**
 * Load all employees and make it possible to manage them
 */
class EmployeeOverviewStore extends BaseOverviewStore<any> {
    protected storageKey(): string {
        return 'employee';
    }

    // Implement required abstract methods
    protected defaultSortField(): string {
        return 'id';
    }

    protected getApiEndpoint(): string {
        return 'employee';
    }

    protected getApiPath(): string {
        return 'query';
    }

    // Employee-specific properties
    public available_managers: Array<{ id: number; name: string }> = [];
    public available_trainings: Array<{ id: number; name: string }> = [];
    public filterManager: any = null;
    public filterTraining: any = null;
    public filterTrainingStatus: number = 1;
    public refreshTimer: any = null;

    constructor(sdk: any) {
        super(sdk);

        // Make employee-specific properties observable
        makeObservable(this, {
            filterManager: observable,
            filterTraining: observable,
            filterTrainingStatus: observable,
            available_managers: observable,
            available_trainings: observable,
            applyFilterManager: action,
            applyFilterTraining: action,
            applyFilterTrainingStatus: action,
            allEmployees: computed,
            filterTrainingLabel: computed,
            refreshTimer: observable,
            resetRefreshTimer: action,
            refresh: action,
        });

        this.loadFromLocalStorage();
    }

    public resetRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    public refresh(): void {
        if (this.refreshTimer) {
            return;
        }
        const that = this;
        this.refreshTimer = setTimeout(() => that.resetRefreshTimer(), 5000);
        this.fetchList();
    }

    protected buildApiParams(page: number): any {
        return {
            sortvalue: this.sortField,
            sort: this.sortOrder === 1 ? 'asc' : 'desc',
            search: this.searchValue || '',
            page,
            ...(this.filterManager
                ? { filter: { type: 'manager', value: this.filterManager.id } }
                : {}),
            ...(this.filterTraining
                ? {
                      filterTraining: {
                          id: this.filterTraining.id,
                          passed: this.filterTrainingStatus === 1,
                      },
                  }
                : {}),
        };
    }

    protected processApiResponse(response: ApiResponse): any[] {
        return response.json.data;
    }

    protected getStorageState(): EmployeeStoreState {
        return {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterManager: this.filterManager,
            filterTraining: this.filterTraining,
            filterTrainingStatus: this.filterTrainingStatus,
        };
    }

    protected applyStorageState(state: EmployeeStoreState): void {
        runInAction(() => {
            this.sortOrder = state.sortOrder || 1;
            this.sortField = state.sortField || this.defaultSortField();
            this.searchValue = state.searchValue || '';
            this.filterManager = state.filterManager || null;
            this.filterTraining = state.filterTraining || null;
            this.filterTrainingStatus = state.filterTrainingStatus || 1;
        });
    }

    // Employee-specific filter methods
    public applyFilterManager(manager: any): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterTraining(training: any): void {
        this.filterTraining = training;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterTrainingStatus(status: any): void {
        this.filterTrainingStatus = status;
        this.saveToLocalStorage();
        this.fetchList();
    }

    // Employee-specific API methods
    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force && this.available_managers.length > 1) {
            return;
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: formatName(manager.firstname, manager.lastname),
                }));
            }
        });
    }

    public async fetchAvailableTrainings(force: boolean = false): Promise<void> {
        if (!force && this.available_trainings.length > 1) {
            return;
        }
        const trainingResponse: any = await this.sdk.fetchApi('training', 'query', {});

        runInAction(() => {
            if (trainingResponse?.status === 200) {
                this.available_trainings = trainingResponse.json.data.map((training: any) => ({
                    id: training.id,
                    name: training.name,
                }));
            }
        });
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'employee' && event?.event === 'SAVED') {
            await this.fetchList(this.latestPage);
        } else {
            await super.onDialogLeave(dialog, event);
        }
    }

    public onLeaveStore(): void {
        super.onLeaveStore();

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }

    // Computed properties and aliases for compatibility
    get allEmployees(): Array<any> | null {
        return this.allItems;
    }

    get employeeList(): Array<any> {
        return this.list;
    }

    get employeeListLoading(): boolean {
        return this.listLoading;
    }

    public fetchEmployeeList(page: number = 1): Promise<void> {
        return this.fetchList(page);
    }

    get filterTrainingLabel(): string | null {
        if (this.filterTraining) {
            return `${this.filterTraining.name} (${this.filterTrainingStatus === 1 ? 'Bestanden' : 'Nicht bestanden'})`;
        }
        return null;
    }
}

export default EmployeeOverviewStore;
